import { Form, Input, message, Modal, Button, Table } from 'antd';
import LOCALS from 'commons/locals';
import { useCallback, useEffect, useMemo, useState } from 'react';
import i18n from 'i18n';
import { CmsApi, CmsSubject } from 'apis/cms';
import { getProductListNew, PmsProductEs } from 'apis/pms';
import { ColumnsType } from 'antd/es/table';
import { uniqBy } from 'lodash-es';

interface Props {
  open: boolean;
  onClose: () => void;
  data?: CmsSubject;
  onLoad?: () => void;
}

const CmsSubjectProductAddModal = ({ open, onClose, data, onLoad }: Props) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [productOptionList, setProductOptionList] = useState<PmsProductEs[]>(
    []
  );
  const [selectedRows, setSelectedRows] = useState<PmsProductEs[]>([]);
  const onFinish = async () => {
    setLoading(true);
    const list =
      selectedRows.map((d) => ({
        subjectId: data?.id as number,
        productId: d.id,
      })) || [];
    const oldList =
      data?.cmsSubjectProductRelations.map((d) => ({
        id: d.id,
        subjectId: d.subjectId,
        productId: d.productId,
      })) || [];
    const cmsSubjectProductRelations = list.concat(oldList);
    CmsApi.subjectEdit({
      ...data,
      cmsSubjectProductRelations: uniqBy(
        cmsSubjectProductRelations,
        'productId'
      ),
    })
      .then(() => {
        message.success(i18n.t(LOCALS.successful_operation));
        onLoad?.();
        onClose();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (data) {
      form.setFieldsValue({ ...data, showStatus: Boolean(data.showStatus) });
    }
  }, [data, form]);

  const handleSearchProduct = useCallback((keyword: string) => {
    keyword = keyword.trim();
    if (!keyword) {
      setProductOptionList([]);
      return;
    }

    setLoading(true);
    getProductListNew({
      keyword,
      pageNum: 1,
      pageSize: 200,
    })
      .then((data) => {
        setProductOptionList(data.list);
      })
      .catch()
      .finally(() => setLoading(false));
  }, []);

  const columns: ColumnsType<PmsProductEs> = useMemo(() => {
    return [
      {
        title: '商品ID',
        dataIndex: 'id',
        key: 'id',
      },
      {
        title: i18n.t('SYLYoWTeQq'),
        dataIndex: 'description',
        key: 'description',
        render: (description: PmsProductEs['description']) => {
          return description || '-';
        },
      },
      {
        title: i18n.t('product_name_1'),
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: i18n.t('list_remark'),
        dataIndex: 'note',
        key: 'note',
      },
    ];
  }, []);

  return (
    <Modal
      open={open}
      title={i18n.t('add')}
      onCancel={() => onClose()}
      onOk={onFinish}
      destroyOnClose
      confirmLoading={loading}
      width={1000}
    >
      <Form
        form={form}
        layout="inline"
        onFinish={({ keyword }) => {
          handleSearchProduct(keyword);
        }}
      >
        <Form.Item name="keyword">
          <Input.TextArea
            className="w-96"
            rows={5}
            placeholder={i18n.t('please_enter') || ''}
          />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading}>
            {i18n.t('search')}
          </Button>
        </Form.Item>
      </Form>

      <Table
        tableLayout="fixed"
        bordered
        rowSelection={{
          selectedRowKeys: selectedRows.map((d) => d.id) as React.Key[],
          onChange: (_, selectedRows) => setSelectedRows(selectedRows),
        }}
        loading={loading}
        rowKey={'id'}
        style={{
          marginTop: 12,
        }}
        dataSource={productOptionList}
        columns={columns}
      />
    </Modal>
  );
};

export default CmsSubjectProductAddModal;
